import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiErr, inr } from "@/lib/api";

const BRAND = "#d81f26";

const RR_LABEL = {
  requested: "Requested", approved: "Approved", refund_initiated: "Refund Pending (Bank)",
  refund_completed: "Refund Done", replacement_initiated: "Replacement Sent", rejected: "Rejected",
};
const RR_COLOR = {
  requested: "#0EA5E9", approved: "#6366F1", refund_initiated: "#F59E0B",
  refund_completed: "#16A34A", replacement_initiated: "#8B5CF6", rejected: "#DC2626",
};

function Pill({ status }) {
  const c = RR_COLOR[status] || "#64748B";
  return <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-extrabold" style={{ color: c, backgroundColor: c + "22" }}>{RR_LABEL[status] || status}</span>;
}

export default function AdminReturns() {
  const [reqs, setReqs] = useState([]);
  const [returns, setReturns] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [busy, setBusy] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(() => {
    api.get("/admin/return-requests").then((r) => setReqs(r.data)).catch(() => {});
    api.get("/admin/returns").then((r) => setReturns(r.data)).catch(() => {});
    api.get("/admin/refunds").then((r) => setRefunds(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (rid, action, body) => {
    setBusy(rid + action);
    try {
      const { data } = await api.post(`/admin/return-requests/${rid}/${action}`, body || {});
      let msg = "Done";
      if (action === "reject") msg = "Return rejected";
      else if (action === "mark-paid") msg = "Refund marked paid ✓";
      else if (data?.status === "refund_completed") msg = "Approved — refund done ✓";
      else if (data?.status === "refund_initiated") msg = "Approved — bank refund pending (Mark Paid baaki)";
      else if (data?.status === "replacement_initiated") msg = "Approved — replacement bheja jayega";
      else msg = "Return approved";
      toast.success(msg);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };
  const reject = (rid) => { setRejectReason(""); setRejectId(rid); };
  const confirmReject = async () => { const rid = rejectId; setRejectId(null); await act(rid, "reject", { reason: rejectReason.trim() }); };

  const openReqs = reqs.filter((r) => r.status === "requested");
  const otherReqs = reqs.filter((r) => r.status !== "requested");

  return (
    <div className="space-y-6" data-testid="admin-returns-page">
      <div className="flex items-center gap-2">
        <span className="w-1.5 h-6 rounded-full" style={{ backgroundColor: BRAND }} />
        <h1 className="text-2xl font-bold">Returns &amp; Refunds</h1>
      </div>

      <section>
        <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-2">Customer Return Requests ({openReqs.length} pending)</h2>
        {reqs.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400" data-testid="no-return-requests">Abhi koi customer return request nahi.</div>
        ) : (
          <div className="space-y-3">
            {[...openReqs, ...otherReqs].map((r) => (
              <div key={r.id} data-testid={`rr-${r.id}`} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-extrabold">#{r.order_no}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{new Date(r.created_at).toLocaleString("en-IN")} · {r.resolution === "replacement" ? "Replacement" : `Refund via ${r.refund_method}`}</p>
                  </div>
                  <Pill status={r.status} />
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(r.items || []).map((it, i) => (
                    <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-lg px-2 py-1">
                      {it.image ? <img src={it.image} alt="" className="w-8 h-8 rounded object-cover" /> : <div className="w-8 h-8 rounded bg-slate-200" />}
                      <span className="text-xs font-semibold">{it.name} × {it.qty}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-sm text-slate-600"><b>Reason:</b> {r.reason}{r.remarks ? ` — ${r.remarks}` : ""}</div>
                {Array.isArray(r.photos) && r.photos.length > 0 && (
                  <div className="flex gap-2 mt-2">{r.photos.map((p, i) => <img key={i} src={p} alt="" className="w-14 h-14 rounded-lg object-cover" />)}</div>
                )}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 flex-wrap gap-2">
                  <span className="font-extrabold" style={{ color: BRAND }}>{inr(r.refund_amount)}</span>
                  <div className="flex gap-2">
                    {r.status === "requested" && (
                      <>
                        <button data-testid={`rr-reject-${r.id}`} disabled={busy} onClick={() => reject(r.id)} className="px-4 py-1.5 border border-rose-200 text-rose-600 rounded-lg text-xs font-bold hover:bg-rose-50 disabled:opacity-50">Reject</button>
                        <button data-testid={`rr-approve-${r.id}`} disabled={busy} onClick={() => act(r.id, "approve")} className="px-4 py-1.5 text-white rounded-lg text-xs font-bold disabled:opacity-50" style={{ backgroundColor: BRAND }}>{busy === r.id + "approve" ? "…" : "Approve & Refund"}</button>
                      </>
                    )}
                    {r.status === "refund_initiated" && (
                      <button data-testid={`rr-markpaid-${r.id}`} disabled={busy} onClick={() => act(r.id, "mark-paid")} className="px-4 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-bold disabled:opacity-50">Mark Paid</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 font-bold">Processed Returns ({returns.length})</div>
          <table className="w-full text-sm"><thead><tr className="text-xs font-bold uppercase text-slate-500 bg-slate-50"><th className="text-left py-2.5 px-4">Order</th><th className="text-left py-2.5 px-4">Reason</th><th className="text-right py-2.5 px-4">Refund</th></tr></thead>
            <tbody>{returns.map((r) => <tr key={r.id} data-testid={`return-${r.id}`} className="border-b border-slate-50"><td className="py-2.5 px-4 font-semibold">{r.order_no}</td><td className="py-2.5 px-4 text-slate-500">{r.reason}</td><td className="py-2.5 px-4 text-right font-semibold">{inr(r.refund_amount)}</td></tr>)}
            {!returns.length && <tr><td colSpan="3" className="text-center py-8 text-slate-400">No returns yet</td></tr>}</tbody></table>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 font-bold">Refunds ({refunds.length})</div>
          <table className="w-full text-sm"><thead><tr className="text-xs font-bold uppercase text-slate-500 bg-slate-50"><th className="text-left py-2.5 px-4">Order</th><th className="text-left py-2.5 px-4">Method</th><th className="text-left py-2.5 px-4">Status</th><th className="text-right py-2.5 px-4">Amount</th></tr></thead>
            <tbody>{refunds.map((r) => <tr key={r.id} className="border-b border-slate-50"><td className="py-2.5 px-4 font-semibold">{r.order_id?.slice(-8)}</td><td className="py-2.5 px-4 text-slate-500">{r.method || "—"}</td><td className="py-2.5 px-4"><span className={`text-xs font-bold ${r.status === "processed" ? "text-emerald-600" : "text-amber-600"}`}>{r.status}</span></td><td className="py-2.5 px-4 text-right font-semibold">{inr(r.amount)}</td></tr>)}
            {!refunds.length && <tr><td colSpan="4" className="text-center py-8 text-slate-400">No refunds yet</td></tr>}</tbody></table>
        </div>
      </div>

      {rejectId && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="reject-modal" onClick={() => setRejectId(null)}>
          <div className="w-full max-w-md bg-white rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-extrabold">Reject Return Request</h3>
            <p className="text-xs text-slate-500 mt-0.5 mb-3">Customer ko ye reason dikhega (optional).</p>
            <textarea data-testid="reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3} placeholder="e.g. Return window over / product used…" autoFocus className="w-full rounded-xl border border-slate-200 p-3 text-sm resize-none focus:outline-none focus:ring-2" style={{ "--tw-ring-color": BRAND }} />
            <div className="flex gap-3 mt-4">
              <button data-testid="reject-cancel" onClick={() => setRejectId(null)} className="flex-1 h-11 rounded-xl border border-slate-200 font-bold text-slate-700">Cancel</button>
              <button data-testid="reject-confirm" onClick={confirmReject} className="flex-1 h-11 rounded-xl text-white font-bold" style={{ backgroundColor: BRAND }}>Reject Return</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
